import type { PaymentMethod } from '@lucidtech/las-sdk-browser';
import { useNavigate } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo } from 'react';

import { Badge, Button, Flex, Heading, Table, Text } from '@/components/core';
import { AIModelIcon, DocumentsIcon, UsersIcon } from '@/components/icons';
import { PaymentPlanLogo } from '@/components/logos';
import { CreditCard } from '@/components/ui';
import { toPlanId, useDeletePaymentMethod, useGetPlan, useListPaymentMethods } from '@/hooks/api';
import { useOrganizationStore } from '@/store';
import { Id } from '@/utils';

import { AddPaymentMethod } from './AddPaymentMethod.tsx';
import { Section, SectionCard } from './Section.tsx';
import { UsageCard } from './UsageCard.tsx';

export type BillingDetailsProps = object;

// eslint-disable-next-line no-empty-pattern
const IBillingDetails = ({}: BillingDetailsProps = {}) => {
  const paymentMethodId = useOrganizationStore((state) => state.paymentMethodId);
  const update = useOrganizationStore((state) => state.update);
  const { data: paymentMethods } = useListPaymentMethods();
  const { mutate: deletePaymentMethod } = useDeletePaymentMethod();

  const planId = useOrganizationStore((state) => state.planId);
  const { data: currentPlan } = useGetPlan(toPlanId(planId));

  const paymentMethodsColumns = useMemo<ColumnDef<PaymentMethod & Id>[]>(() => {
    return [
      {
        accessorKey: 'details',
        cell: ({ row }) => {
          return <CreditCard className="ml-4" details={row.original.details} />;
        },
        size: Number.MAX_SAFE_INTEGER,
      },
      {
        accessorKey: 'paymentMethodId',
        cell: ({ row }) => {
          if (row.original.paymentMethodId === paymentMethodId) {
            return <Badge className="mr-4" variant="primary" label="Active" />;
          }
          const details = row.original.details;
          if (!details) return null;
          const expires = new Date(details.expYear, details.expMonth);
          if (expires < new Date()) {
            return <Badge className="mr-4" variant="error" label="Expired" />;
          }
        },
        size: 0,
      },
    ];
  }, [paymentMethodId]);

  const paymentMethodsData = useMemo(() => {
    return paymentMethods?.data.filter((p) => p.details).map((p) => ({ ...p, id: p.paymentMethodId })) ?? [];
  }, [paymentMethods]);

  const numberOfAgentsAllowed = useOrganizationStore((state) => state.numberOfAgentsAllowed);
  const numberOfAgentsCreated = useOrganizationStore((state) => state.numberOfAgentsCreated);

  const numberOfUsersAllowed = useOrganizationStore((state) => state.numberOfUsersAllowed);
  const numberOfUsersCreated = useOrganizationStore((state) => state.numberOfUsersCreated);

  const monthlyNumberOfPagePredictionsAllowed = useOrganizationStore(
    (state) => state.monthlyNumberOfPagePredictionsAllowed
  );
  const monthlyNumberOfPagePredictionsUsed = useOrganizationStore((state) => state.monthlyNumberOfPagePredictionsUsed);

  const navigate = useNavigate();

  return (
    <>
      <Section title="Current plan" description="Monthly billing cycle">
        <SectionCard>
          <PaymentPlanLogo planId={planId} size="xl" />

          <Flex className="w-full items-center">
            <Flex col>
              <Heading size="sm">{currentPlan?.name}</Heading>
              <Text>${currentPlan?.license?.flatAmount?.toString()}</Text>
            </Flex>
            <div className="grow" />
            <Button variant="secondary" onPress={async () => navigate({ to: '/settings/plans' })}>
              <Text>Change</Text>
            </Button>
          </Flex>
        </SectionCard>
      </Section>

      <Section title="Usage" description="Your current usage this billing cycle.">
        <div className="gap-sm grid grid-cols-3">
          <UsageCard
            resource="Seats"
            used={numberOfUsersCreated}
            available={numberOfUsersAllowed}
            icon={<UsersIcon />}
          />

          <UsageCard
            resource="Agents"
            used={numberOfAgentsCreated}
            available={numberOfAgentsAllowed}
            icon={<AIModelIcon />}
          />

          <UsageCard
            resource="Pages"
            used={monthlyNumberOfPagePredictionsUsed}
            available={monthlyNumberOfPagePredictionsAllowed}
            icon={<DocumentsIcon />}
          />
        </div>
      </Section>

      <Flex col className="gap-sm items-start">
        <Section title="Payment methods" description="Add or update your payment method">
          {paymentMethodsData.length > 0 && (
            <SectionCard className="p-0">
              <Table
                columns={paymentMethodsColumns}
                className="border-separate border-spacing-x-0"
                data={paymentMethodsData}
                hideDragHandle
                hideHeader
                options={[
                  {
                    onAction: (row) => update({ paymentMethodId: row.original.paymentMethodId }),
                    children: <Text>Set as active</Text>,
                  },
                ]}
                deleteRowAction={(row) => deletePaymentMethod({ paymentMethodId: row.original.paymentMethodId })}
                props={{ row: { className: 'hover:bg-gray-50 rounded-lg' } }}
              />
            </SectionCard>
          )}
        </Section>

        <AddPaymentMethod />
      </Flex>
      <Section title="Invoices" description="Invoices are automatically emailed to the account owner" />
    </>
  );
};

export const BillingDetails = memo(IBillingDetails) as typeof IBillingDetails;
